import { Wretch } from 'wretch'

import { ApiErrorResponse } from '../types/responses/ApiResponse.js'
import { LoginResponse } from '../types/responses/auth/LoginResponse.js'
import { isWretchError } from '../utils.js'

export type Credentials = {
  email: string
  password: string
}

export abstract class Authorization {
  public currentNewTokenPromise: Promise<string> | null = null
  private onNewTokenCallbacks: Array<
    (token: string, client: Wretch) => unknown
  > = []
  private onTokenExpiredCallbacks: Array<() => unknown> = []
  private onRefreshTokenExpiredCallbacks: Array<() => unknown> = []
  private onAuthorizationFailCallbacks: Array<(e: Error) => unknown> = []
  private onLoggedInCallbacks: Array<() => unknown> = []

  protected client!: Wretch
  public currentJWT: string | null = null

  /**
   * @param jwtToken If you've resolved token before then you can use it on initialization
   * to not send auth request needlessly
   */
  constructor(private initJWTToken: string | null = null) {
    if (initJWTToken) {
      this.setAuthToken(initJWTToken)
    }
  }

  protected async setAuthToken(token: string): Promise<void> {
    this.currentJWT = token

    await this.executeNewTokenCallbacks(token)
  }

  public clearCallbacks(): void {
    this.onNewTokenCallbacks = []
    this.onTokenExpiredCallbacks = []
    this.onRefreshTokenExpiredCallbacks = []
    this.onAuthorizationFailCallbacks = []
    this.onLoggedInCallbacks = []
  }

  public async init(client: Wretch): Promise<void> {
    this.client = client

    if (this.initJWTToken) {
      const initJWTToken = this.initJWTToken

      this.currentNewTokenPromise = new Promise<string>((resolve) => {
        setTimeout(async () => {
          this.currentNewTokenPromise = null
          for (const callback of this.onNewTokenCallbacks) {
            await callback(initJWTToken, this.client)
          }
          resolve(initJWTToken)
        })
      })
    } else {
      await this.authorize()
    }
  }

  abstract reauthorize(): Promise<string | undefined>

  abstract requestCredentials(): Promise<Credentials>

  public onRefreshTokenExpired(callback: () => unknown, prepend = false): void {
    if (prepend) {
      this.onRefreshTokenExpiredCallbacks.unshift(callback)
    } else {
      this.onRefreshTokenExpiredCallbacks.push(callback)
    }
  }

  public offRefreshTokenExpired(callback: () => unknown): void {
    this.onRefreshTokenExpiredCallbacks =
      this.onRefreshTokenExpiredCallbacks.filter(
        (registered) => registered !== callback,
      )
  }

  public onTokenExpired(callback: () => unknown, prepend = false): void {
    if (prepend) {
      this.onTokenExpiredCallbacks.unshift(callback)
    } else {
      this.onTokenExpiredCallbacks.push(callback)
    }
  }

  public onAuthorizationFail(callback: (e: Error) => unknown): void {
    this.onAuthorizationFailCallbacks.push(callback)
  }

  public onLoggedIn(callback: () => unknown): void {
    this.onLoggedInCallbacks.push(callback)
  }

  protected executeRefreshTokenExpirationCallbacks(): void {
    for (const callback of this.onRefreshTokenExpiredCallbacks) {
      callback()
    }
  }

  protected async executeNewTokenCallbacks(token: string): Promise<void> {
    for (const callback of this.onNewTokenCallbacks) {
      await callback(token, this.client)
    }
  }

  protected async executeLoggedInCallbacks(): Promise<void> {
    for (const callback of this.onLoggedInCallbacks) {
      await callback()
    }
  }

  public onNewToken(
    callback: (token: string, client: Wretch) => unknown,
    prepend = false,
  ): void {
    if (prepend) {
      this.onNewTokenCallbacks.unshift(callback)
    } else {
      this.onNewTokenCallbacks.push(callback)
    }
  }

  public offNewToken(
    callback: (token: string, client: Wretch) => unknown,
  ): void {
    this.onNewTokenCallbacks = this.onNewTokenCallbacks.filter(
      (registered) => registered !== callback,
    )
  }

  public async login(
    email: string,
    password: string,
  ): Promise<{
    token: string
    refreshToken: string
  }> {
    try {
      const response: LoginResponse = await this.client
        .url('/auth/login')
        .post({ email, password })
        .json<LoginResponse>()

      this.currentNewTokenPromise = null
      await this.executeNewTokenCallbacks(response.api.data.token)

      return {
        token: response.api.data.token,
        refreshToken: response.api.data.refresh_token,
      }
    } catch (e) {
      if (isWretchError(e) && e.text) {
        try {
          const response: ApiErrorResponse = JSON.parse(e.text)
          const parsedError = Error(response.api.error.message)
          for (const callback of this.onAuthorizationFailCallbacks) {
            await callback(parsedError)
          }

          throw parsedError
        } catch (err) {
          throw err as Error
        }
      } else {
        throw e as Error
      }
    }
  }

  async authorize(): Promise<string> {
    if (this.currentNewTokenPromise) {
      return await this.currentNewTokenPromise
    }

    for (const callback of this.onTokenExpiredCallbacks) {
      await callback()
    }

    this.currentNewTokenPromise = new Promise<string>(
      // eslint-disable-next-line no-async-promise-executor
      async (resolve, reject) => {
        const credentials: Credentials = await this.requestCredentials()
        try {
          const { token } = await this.login(
            credentials.email,
            credentials.password,
          )
          resolve(token)
        } catch (e) {
          let parsedError: Error

          if (isWretchError(e) && e.text) {
            try {
              const response: ApiErrorResponse = JSON.parse(e.text)
              parsedError = Error(response.api.error.message)
            } catch (err) {
              parsedError = err as Error
            }
          } else {
            parsedError = e as Error
          }

          for (const callback of this.onAuthorizationFailCallbacks) {
            await callback(parsedError)
          }

          return reject(e)
        }
      },
    ).catch((e) => {
      if (isWretchError(e) && (e.status === 404 || e.status === 500)) {
        throw e
      }

      // eslint-disable-next-line no-console
      console.error(e)
      this.currentNewTokenPromise = null
      return this.authorize()
    })

    return this.currentNewTokenPromise
  }
}
